/* © 2014 - Copyright of Aetonix Systems Inc - All Rights Reserved. Patent pending.
 * Unauthorized copying of this file, via any medium is strictly prohibited
 * Proprietary and confidential
 * Written by Aetonix, June 19, 2014
 * For information or permission request, email info@aetonixsystems.com
 */



var Model = require("../types/Model.js");
var xtend = require("xtend");
var par = require("par");
var Promise = require("promise");
var isObject = require("isobject");
var ReactNative = require("react-native");
var AppState = ReactNative.AppState;
var Platform = ReactNative.Platform;
var NativeModules = ReactNative.NativeModules;
var ShortcutBadger = NativeModules.ShortcutBadger;
import { difference } from "lodash";

const { Notifications } = require("react-native-notifications");

var BANNER_MATCHES = [
	/^urgency/,
	/^warning:device:(?:battery|batterychanged)/,
	/^warning:device:(?:connected|disconnected|wandering)/
];

var NON_CLEARABLE_BANNERS = [
	"urgency:call",
	"urgency:accepted",
	"urgency:cancelled",
	"warning:inactive",
	"warning:device:disconnected",
	"warning:device:connected",
	"warning:device:wandering",
	"warning:device:acknowledge",
	"warning:device:battery",
	"warning:device:batterychanged",
	"warning:device:acknowledge:invalid",
	"info:meeting:new"
];

/**
 * This model is used for tracking a person's unread notifications(call, conference, text, banner).
 * A notification contains all of the calls, conference, text and banners that a person has received
 * @type {Model}
 */
module.exports = Model({
	name: "unreadnotifications",
	init: init,
	uninit: uninit,

	_history: null,
	_blocking: null,
	_people: null,
	_ready: false,
	_groups: null,

	listeners: listeners,
	triggers: triggers,

	refresh: refresh,
	add_ids: add_ids,
	recalculate: recalculate,
	filter_out: filter_out,
	dispatch_type: dispatch_type
});

function triggers() {
	return {
		refresh: refresh,
	};
}


function listeners() {
	return {
		"livechatgroups:updated": handle_livechatgroups_updated,
		"groups:updated": handle_groups_updated,
		"blocking:updated": handle_blocking_updated,
		"people:updated": handle_people_updated,
		"connectivity:online": refresh,
		"mqtt:$/notifications/cleared": handle_clear_notification,
		"mqtt:$/notifications/cleared/all": handle_clear_all_notifications,
		"mqtt:$/notifications/new": handle_add_notification,
		"mqtt:$/notifications/cleared/from": handle_clear_from,
		"mqtt:$/banner/read": handle_read_banner,
		"loadchecker:ready": initial_load,
		"localization:updated": handle_localization_updated,
		"mqtt:$/managegroup/added": refresh,
		"mqtt:$/managegroup/removed": refresh,
	};
}

function initial_load() {
	this._ready = true;
	this.refresh();
}

function init() {
	this.push([]);
	this._history = [];
	this._blocking = [];
	this._people = {};
	this._groups = {};
	this._language = "";
}

function uninit() {
	this._history = null;
	this._blocking = null;
	this._people = null;
	this._groups = null;
}


function refresh() {
	if (!this._ready) return Promise.resolve([]);
	this.emit("save:trigger:start");
	return this.api.notifications.list.unread()
		.then(handle_load.bind(this))
		.catch(console.error.bind(console))
		.then(finishLoad.bind(this));
}

function handle_load(list) {
	this._history = list;
	this.add_ids(list);
	this.recalculate();
}

function finishLoad() {
	this.emit("save:trigger:finish");
}

function handle_localization_updated(localization) {
	this._language = localization.language;
}

function handle_clear_notification(data) {
	var notification = data.notification;
	var currentlyTracking = this._history;
	var everythingExceptTheClearedOne = currentlyTracking.filter(function (item) {
		var isNotification = item._id === notification;
		if (isNotification) this.emit("readnotifications:trigger:add", item);
		return !isNotification;
	}.bind(this));

	this._history = everythingExceptTheClearedOne;
	this.recalculate();
}

function handle_clear_all_notifications(data) {
	if (!data.amount) return;
	var newHistory = this._history.filter(function (item) {
		var isBanner = item.type.indexOf("banner") !== -1;
		var isNonClearableBanner = isBanner && NON_CLEARABLE_BANNERS.indexOf(item.banner_type) !== -1;
		if (!isNonClearableBanner) this.emit("readnotifications:trigger:add", item);
		return isNonClearableBanner;
	}.bind(this));
	this._history = newHistory;
	this.recalculate();
}

function handle_clear_from(data) {
	var type = data.type;
	var from = data.from;
	var localState = this._history;
	var newList = localState.filter(function (item) {
		if (item.type.indexOf(type) === -1) return true;
		var isNotification = true;
		if (!is_group(item)) isNotification = Array.isArray(item.from) ? (item.from.indexOf(from) !== -1) : item.from === from;
		else if(is_livechat(item)) isNotification = item.from === from;
		else if(is_group(item)) isNotification = isObject(item.group) ? item.group._id === from : item.group === from;
		if (isNotification) this.emit("readnotifications:trigger:add", item);
		return !isNotification;
	}.bind(this));

	this._history = newList;

	this.recalculate();
}

function handle_add_notification(notification) {
	var existing = this._history;
	this._history = [notification].concat(existing);
	this.add_ids(this._history);
	this.dispatch_type(notification);
	this.recalculate();
}

function recalculate() {
	var people = this._people;
	var history = this._history;
	var blocking = this._blocking;
	var groups = this._groups;
	var language = this._language;
	var livechats = this._livechats;

	var merged = history.reduce(function (list, item) {
		var content = item.content || {};
		var update = {
			content: process_content(content),
			updated_at: timestamp(item.updated_at),
			formatted_date: formatDate(item.updated_at, checkLanguage(language)),
		};
		var from = item.from;
		var to = content.to;
		var acknowledge = content.acknowledge;

		if (is_conference(item)) {
			var from0 = from[0];
			var from1 = from[1];
			if (blocking.indexOf(from0) !== -1) return list;
			if (blocking.indexOf(from1) !== -1) return list;

			var person0 = people[from0];
			var person1 = people[from1];

			if (!(person0 && person1)) return list;

			update.from = [person0, person1];
		} else if (is_livechat(item)) {
			if (item.type.indexOf("systemmessage") !== -1) {
				var systemPerson = item.metadata.data;
				update.from = "system";
				var personObj = people[systemPerson];
				if (!personObj) return list;
				update.metadata = {
					type: item.metadata.type,
					data: personObj
				};
			} else {
				var person = people[from];
				if(!person) return list;
				update.from = person;
			}
			update.read = false;
			var group = livechats[item.group];
			if(!group) return list;
			update.group = group;
		}	else if (is_group(item)) {
			if (item.type.indexOf("systemmessage") !== -1) {
				var systemPerson = item.metadata.data;
				update.from = "system";
				var personObj = people[systemPerson];
				if (!personObj) return list;
				update.metadata = {
					type: item.metadata.type,
					data: personObj
				};
			} else {
				var member = people[from];
				if (!member) return list;
				update.from = member;
			}
			update.read = false;
			var group = groups[item.group];
			if (!group) return list;
			update.group = group;
		} else {
			if (blocking?.indexOf(from) !== -1) return list;
			var person = people[from];
			if (!person) return list;
			update.from = person;
			if (blocking.indexOf(to) !== -1) return list;
			if (to) {
				var personTo = people[to];
				if (!personTo) return list;
				update.content = xtend(content, {
					to: personTo
				});
			}
			if (blocking.indexOf(acknowledge) !== -1) return list;
			if (acknowledge) {
				var personAcknowledge = people[acknowledge];
				update.content = xtend(content, {
					acknowledge: personAcknowledge
				});
			}
		}

		list.push(xtend(item, update));

		return list;
	}, []);

	var badgeNumber = merged.length;

	if (Platform.OS === "ios" && AppState.currentState === "active") {
		Notifications.ios.setBadgeCount(badgeNumber);
	}

	if (Platform.OS === "android") {
		ShortcutBadger.applyCount(badgeNumber);
	}

	this.push(merged);
}


function add_ids(list) {
	var ids = get_ids(list);
	this.emit("people:trigger:add", {
		who: ids,
		lean: true
	});
}

/**
 * Fetches out all the unique ids in a list of timline items
 * @param  {Array} list The raw page items, each having a `from` property
 * @return {Array}      List of unique `from` ids in the items
 */
function get_ids(list) {
	return Object.keys(list.reduce(function (result, item) {
		var from = item.from;
		var content = item.content || {};
		var to = content.to;
		var acknowledge = content.acknowledge;
		var metadata = item.metadata;
		if (is_conference(item)) {
			result[from[0]] = true;
			result[from[1]] = true;
		} else result[from] = true;

		if(to && Array.isArray(to)){
			to.forEach(function(id){
				result[id] = true;
			});
		} else if (to) result[to] = true;
		if (acknowledge) result[acknowledge] = true;

		if (item.type.indexOf("systemmessage") && metadata) {
			var data = item.metadata.data;
			result[data] = true;
		}

		return result;
	}, {}));
}

function checkLanguage(lang) {
	if (lang === "cn_s")
		return "zh-CN";
	else if (lang === "cn_t")
		return "zh-HK";
	else
		return lang;
}

// Converts a date timestamp into a human readable timestamp
function timestamp(value) {
	if (!value) return ""; // Ignore empty values
	var d = new Date(value);
	var res = "";
	res += padNumber(d.getHours());
	res += ":" + padNumber(d.getMinutes());
	return res;
}

// Converts a date timestamp into a human readable date
function formatDate(value, language) {
	if (!value) return ""; // Ignore empty values
	var d = new Date(value);
	return d.toLocaleDateString(language, getDateLocaleOptions());
}

function getDateLocaleOptions() {
	return {
		day: "numeric",
		month: "short"
	};
}

// Pads numbers less than 10 with a 0
function padNumber(number) {
	if (number < 10) return "0" + number;
	return number;
}

function is_conference(item) {
	return item.type.indexOf("conference") > -1;
}

function is_group(item) {
	return item.type.indexOf("groupmessage") !== -1;
}

function is_livechat(item){
	return item.type.indexOf("livechat:message") !== -1;
}

function handle_people_updated(people) {
	this._people = people;
	this.recalculate();
}

function handle_blocking_updated(blocking) {
	this._blocking = blocking;
	this.recalculate();
}

function handle_groups_updated(groups) {
	this._groups = process_groups(groups);
	this.recalculate();
}

function handle_livechatgroups_updated(groups){
	this._livechats = process_groups(groups);
	this.recalculate();
}

function process_groups(groups) {
	return groups.reduce(function (newGroups, group) {
		var id = group._id;
		newGroups[id] = group;
		return newGroups;
	}, {});
}

function process_content(content) {
	if (!content) return content;
	if (typeof content === "string") return content;
	if (content.hangup) return xtend(content, {
		hangup: timestamp(content.hangup)
	});
	return content;
}

function handle_read_banner(data) {
	var from = data.from;
	var type = data.type;
	var filtered = filter_out(this._history, from, type);
	const diff = difference(this._history, filtered)

	this._history = filtered;
	this.add_ids(this._history);
	if(diff?.length) {
		diff.forEach(notification => {
			this.emit("readnotifications:trigger:add", notification);
		})
	}
	this.recalculate();
}

/**
 * Removes banners from a given user of a given type
 * Triggers an update when done
 * @param  {String} from The id of the person to clear data from
 */
function filter_out(notifications, from, type) {
	var match = getMatcher(type);
	return notifications.filter(par(filter_banner, from, match));
}

function filter_banner(from, match, notification) {
	if (notification.type.indexOf("banner") === -1) return true;

	var bannerFrom = notification.from;
	var type = notification.banner_type;

	if (bannerFrom !== from) return true;
	if (!type.match(match)) return true;
	return false;
}

function getMatcher(type) {
	if (!type) {
		return;
	}
	return BANNER_MATCHES.reduce(function (result, match) {
		if (result) return result;
		if (type.match(match)) return match;
		return null;
	}, null) || type;
}

function dispatch_type(notification) {
	var type = notification.type;
	if (type.indexOf("text") !== -1)
		this.dispatch("newmessage");
	if (type.indexOf("call") !== -1)
		this.dispatch("newcall");
}
